import { Icon, MenuDropdown, tokens, Typography } from "@vintecc/components";
import classNames from "classnames";
import styles from "./Topbar.module.css";
import { Link, useLocation, useNavigate, useRouterState } from "@tanstack/react-router";
import { ENV } from "@/env";
import { useBreakpoint } from "@/shared/hooks/useBreakpoint";
import { useAppStore } from "@/store/appStore";
import CompanySelect from "./CompanySelect";
import { useDefaultApps } from "@/hooks/useDefaultApps";
import { useMemo } from "react";
import { CAPTURE_ROLES } from "@/constants";
import platformAdmin from "@/api/platformAdmin.api";
import { faCog, faFileCheck, faFileContract, faShieldCheck, faSignOutAlt, faUser } from "@vintecc/pro-light-svg-icons";
import { LazyCaptainPanelTrigger } from "./LazyCaptainPanelTrigger";
import { StyledImage } from "@/shared/components/StyledImageComponent/StyledImageComponent";
import { Loader } from "../Loader/Loader";
import { useCommonTranslation } from "@/services/culture/common.translations";
import { PlatformNotifications } from "./PlatformNotifications/PlatformNotifications";

const Topbar = () => {
  const { t } = useCommonTranslation();
  const { loginAnswer } = useAppStore();
  const navigate = useNavigate();
  const location = useLocation();
  const { isMd: isDesktop } = useBreakpoint();

  const defaultApps = useDefaultApps();
  const userName = loginAnswer?.captureUser?.name ?? t("common.userFallback");
  const userMenuAriaLabel = isDesktop ? `${userName}, ${t("topbar.userMenu")}` : t("topbar.userMenu");

  const currentApp = useMemo(() => {
    if (location.pathname === "/admin")
      return { title: t("topbar.admin"), showTitleInTopbar: true, icon: <Icon icon={faCog} />, color: tokens.theme.default.color };
    return defaultApps.find((app) => app.to && location.pathname.startsWith(app.to) && app.showTitleInTopbar);
  }, [location.pathname, defaultApps, t]);

  const isNavigating = useRouterState({
    select: (state) => state.status === "pending",
  });

  return (
    <div className={classNames(styles.root, "h-full relative flex justify-between items-center")}>
      <div className="flex items-center gap-4">
        <Link to="/" className="logo sticky top-0 z-10 md:hidden shrink-0">
          <StyledImage type="emblem" fallBack="/emblem.svg" width={46} height={46} loader={<Loader />} />
        </Link>
        {currentApp && isDesktop && (
          <div className="flex gap-2 items-center">
            <span className="text-lg" style={{ color: currentApp.color ?? tokens.theme.default.color }}>
              {currentApp.icon}
            </span>
            <Typography variant="h3" as="h1" data-testid="topbar-title">
              {currentApp.title}
            </Typography>
          </div>
        )}
      </div>

      <div className="flex gap-2 items-baseline">
        {/* <MenuDropdown
          text="EN"
          items={[
            { id: "nl-BE", label: "NL" },
            { id: "en-US", label: "EN" },
            { id: "fr-FR", label: "FR" },
          ]}
        /> */}

        <CompanySelect showCompanyName={isDesktop} />

        <MenuDropdown
          testId="user-menu-dropdown"
          aria-label={userMenuAriaLabel}
          icon={<Icon color={tokens.color.grey[400]} icon={faUser} size="lg" />}
          text={isDesktop ? userName : ""}
          // TODO: uncomment when support has been added to Vintecc/components - buttonClassName="h-11 py-3 px-0.75 md:px-0"
          items={[
            /* {
              id: "profile",
              label: t("topbar.profile"),
              startIcon: <Icon icon={faUser} />,
            }, */
            {
              id: "admin",
              label: t("topbar.admin"),
              startIcon: <Icon icon={faCog} />,
              hidden: loginAnswer?.captureUser?.role !== CAPTURE_ROLES.ADMIN,
              onClick: () => {
                navigate({ to: "/admin" });
              },
            },
            // {
            //   id: "theme",
            //   label: theme === "light" ? t("common.darkMode") : t("common.lightMode"),
            //   startIcon: <Icon icon={theme === "light" ? faMoonStars : faSun } />,
            //   onClick: () => setTheme(theme === "light" ? "dark" : "light"),
            // },
            {
              id: "responsible-disclosure",
              label: t("topbar.responsibleDisclosure"),
              startIcon: <Icon icon={faShieldCheck} />,
              hidden: !ENV.DISCLOSURE_URL,
              onClick: () => {
                window.open(ENV.DISCLOSURE_URL, "_blank", "noopener,noreferrer");
              },
            },
            {
              id: "privacy-policy",
              label: t("topbar.privacyPolicy"),
              startIcon: <Icon icon={faFileContract} />,
              hidden: !ENV.PRIVACY_POLICY_URL,
              onClick: () => {
                window.open(ENV.PRIVACY_POLICY_URL, "_blank", "noopener,noreferrer");
              },
            },
            {
              id: "terms-of-use",
              label: t("topbar.termsOfUse"),
              startIcon: <Icon icon={faFileCheck} />,
              hidden: !ENV.TERMS_AND_CONDITIONS_URL,
              onClick: () => {
                window.open(ENV.TERMS_AND_CONDITIONS_URL, "_blank", "noopener,noreferrer");
              },
            },
            {
              id: "logout",
              label: t("topbar.logout"),
              startIcon: <Icon icon={faSignOutAlt} />,
              onClick: async () => {
                await platformAdmin.logout();

                if (ENV.SSO_DISABLED) {
                  navigate({ to: "/login" });
                } else {
                  window.location.href = `${ENV.AUTH_SERVER}/api/logout?redirect=${window.location.origin}`;
                }
              },
            },
          ]}
        />
        <PlatformNotifications />
        <LazyCaptainPanelTrigger />

        {/* <Icon icon={faBell} size="lg" className="mt-0.5" /> */
        /* Disabled notifications UI since this is not yet implemented. */}
      </div>
      <div
        className={classNames(
          styles.progressBar,
          "absolute left-0 right-0 bottom-0 after:block after:h-full after:w-full",
          isNavigating && styles.progressBarActive,
        )}
      />
    </div>
  );
};

export default Topbar;
